import * as tldExtract from "tld-extract";
import lodashGet from "lodash/get";
import lodashMatchesProperty from "lodash/matchesProperty";
import lodashFilter from "lodash/filter";
import lodashDebounce from "lodash/debounce";
import { MI_TO_KM_CONVERSION_FACTOR } from "@/common/constants/maps";

export { _addTargetToLinks as addTargetToLinks };
export { _addTargetToLink as addTargetToLink };
export { _removeHtmlTags as removeHtmlTags };
export { _parseStringToObject as parseStringToObject };
export { _readMoreify as readMoreify };
export { _getArrayValueAverage as getArrayValueAverage };
export { _getUrlDomain as getUrlDomain };
export { _setInfiniteScrollState as setInfiniteScrollState };
export { _isEmptyOrSpaces as isEmptyOrSpaces };
export { _upperCaseFirstLetter as upperCaseFirstLetter };
export { _formatBigNumber as formatBigNumber };
export { _formatFileSize as formatFileSize };
export { _sortArrayByPriority as sortArrayByPriority };
export { _propertyByLanguage as propertyByLanguage };
export { _formatFlagUrl as formatFlagUrl };
export { _embedInfoText as embedInfoText };
export { _getReferences as getReferences };
export { _addCardMoreLess as addCardMoreLess };
export { _formatSearchRadius as formatSearchRadius };
export { _fixDecimalValuesForSlider as fixDecimalValuesForSlider };
export { _cleanUpAddCardMoreLessEvents as cleanUpAddCardMoreLessEvents };

// accepts html text
const _addTargetToLinks = (html, target = "_blank") => {
  const domParser = new DOMParser();
  const document = domParser.parseFromString(html, "text/html");
  const serializer = new XMLSerializer();

  // Handles external links
  const links = document.querySelectorAll("a");

  links.forEach((link) => {
    // if its not an external link then remove the link and
    // just turn it into regular text using a <span> tag
    if (
      link.href &&
      link.href.toLowerCase().includes("http") &&
      !link.href.toLowerCase().includes(window.location.hostname)
    ) {
      link.target = target;
      link.rel = "noopener noreferrer";
    } else if (link.href && link.href.toLowerCase().includes("mailto:")) {
      link.target = target;
      link.rel = "noopener noreferrer";
    } else {
      const newSpan = document.createElement("span");
      newSpan.innerHTML = `[${link.innerHTML}]`;
      link.parentNode.insertBefore(newSpan, link);
      link.parentNode.removeChild(link);
    }
  });

  return serializer.serializeToString(document);
};

// accepts a DOM object
const _addTargetToLink = (link, target = "_blank") => {
  // if its not an external link then remove the link and
  // just turn it into regular text using a <span> tag
  if (
    link.href &&
    link.href.toLowerCase().includes("http") &&
    !link.href.toLowerCase().includes(window.location.hostname)
  ) {
    link.target = target;
    link.rel = "noopener noreferrer";
  } else {
    const newSpan = document.createElement("span");
    newSpan.innerHTML = `[${link.innerHTML}]`;
    link.parentNode.insertBefore(newSpan, link);
    link.parentNode.removeChild(link);
  }
};

const _removeHtmlTags = (html) => {
  return html.replace(/<\/?[^>]+(>|$)/g, " ");
};

const _parseStringToObject = (thing, fieldName) => {
  if (
    thing &&
    fieldName &&
    thing[fieldName] &&
    typeof thing[fieldName] === "string"
  ) {
    thing[fieldName] = JSON.parse(thing[fieldName]);
  }
  return thing;
};

const _readMoreify = (content, link, el, opts) => {
  if (content && el) {
    let prefix = " ...";
    if (opts && opts.prefix) {
      prefix = opts.prefix;
    }
    let linkText = "Read more";
    if (opts && opts.linkText) {
      linkText = opts.linkText;
    }

    let ending =
      prefix +
      "<a href='" +
      link +
      "' target='_blank' rel='noopener'>" +
      linkText +
      "</a>";

    let wordArray = content.split(" ");
    el.innerHTML = wordArray.join(" ") + ending;
    while (el.scrollHeight > el.offsetHeight) {
      wordArray.pop();
      el.innerHTML = wordArray.join(" ") + ending;
    }
  }
};

const _getArrayValueAverage = (arr) => {
  return arr.reduce((a, b) => a + b) / arr.length;
};

const _getUrlDomain = (url) => {
  try {
    let extracted = tldExtract(url);
    if (extracted.domain) {
      return extracted.domain;
    }
  } catch (e) {
    console.error("Failed to format the source URL: ", e);
  }

  return "";
};

const _setInfiniteScrollState = (state, result, field) => {
  if (state && typeof state === "object") {
    if (
      result &&
      field &&
      result.hasOwnProperty(field) &&
      Array.isArray(result[field]) &&
      result[field].length > 1
    ) {
      state.loaded();
    } else {
      state.complete();
    }
  }
};

const _isEmptyOrSpaces = (str) => {
  return str === null || str === undefined || str.match(/^ *$/) !== null;
};

const _upperCaseFirstLetter = (str) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

const _formatBigNumber = (val) => {
  if (val > -1) {
    const MILLION = 1000000;
    const THOUSAND = 1000;
    let formatted;
    if (val > MILLION) {
      formatted = parseFloat((val / MILLION).toFixed(1)) + "M";
    } else if (val > THOUSAND) {
      formatted = parseFloat((val / THOUSAND).toFixed(1)) + "K";
    } else {
      formatted = val;
    }
    return formatted;
  }
  return null;
};

const _formatFileSize = (bytes, decimalPoint) => {
  if (bytes == 0) {
    return "0 Bytes";
  }
  const k = 1000;
  const dm = decimalPoint || 1;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const _sortArrayByPriority = (array, objField, priority) => {
  let sortedArray = [];
  if (array.length > priority) {
    throw new Error("Array is larger than the priority list");
  }
  let arrayMap = {};
  array.forEach((a) => {
    arrayMap[a[objField]] = a;
  });
  priority.forEach((p) => {
    if (p in arrayMap) {
      sortedArray.push(arrayMap[p]);
    }
  });
  return sortedArray;
};

/**
 * Several fields in analysis are broken by language this supports the ability to dynamically display
 * different language but default to "en" when the translation isn't found.
 * @param _obj The array of objects to filter by language
 * @param _language The language code to chose, it will default to en
 * @param property The field you wish to retrieve from the object in the array
 * @returns {string}
 * @private
 */
const _propertyByLanguage = (_obj, _language, property) => {
  let obj = lodashFilter(
    _obj,
    lodashMatchesProperty("language_code", _language)
  );
  if (!obj) {
    return _propertyByLanguage(_obj, "en", property);
  }
  return obj[0][property];
};

/**
 * This is a temporary solution to build the flag_url which is now stored in bg_config countries
 * @param countryCode The country code
 * @returns {string} The full url string to display the image of the flag
 * @private
 */
const _formatFlagUrl = (countryCode) => {
  const BASE = "https://app.seerist.com/attachments/flags/countries/";
  const fileExt = ".png";
  return BASE + countryCode + fileExt;
};

const _embedInfoText = (container, showText, infoText) => {
  const div = document.createElement("div");
  div.style.cursor = "pointer";
  div.style.fontSize = "0.9em";
  div.style.marginBottom = "20px";

  const showContainer = document.createElement("div");
  const showTextNode = document.createTextNode(showText);
  const icon = document.createElement("i");
  icon.className = "v-icon v-icon--link mdi mdi-chevron-right mr-1";
  icon.style = "font-size:16px; position:relative; top:-2px;";
  showContainer.appendChild(showTextNode);
  showContainer.appendChild(icon);

  const infoContainer = document.createElement("i");
  infoContainer.style.display = "none";
  const infoTextNode = document.createTextNode(infoText);
  infoContainer.appendChild(infoTextNode);

  showContainer.appendChild(infoContainer);
  div.appendChild(showContainer);

  div.addEventListener("click", () => {
    if (infoContainer.style.display === "none") {
      infoContainer.style.display = "inline";
      icon.className = "v-icon v-icon--link mdi mdi-chevron-left mr-1";
    } else {
      infoContainer.style.display = "none";
      icon.className = "v-icon v-icon--link mdi mdi-chevron-right mr-1";
    }
  });

  container.appendChild(div);
};

/**
 * Massage the inconsistent data entered by editors as the source for verified events
 * until we have a unified data source; i.e. a link could be entered as the name with no link.
 * @param references
 * @returns {string}
 * @private
 */
const _getReferences = (references) => {
  if (references) {
    let _references = [];
    references.forEach((reference) => {
      let source;
      let name = reference.name?.includes("http")
        ? _getUrlDomain(reference.name)
        : reference.name;
      let url = reference.name?.includes("http")
        ? reference.name
        : reference.url;
      if (url && name) {
        source =
          '<a href="' + url + '" target="_blank" class="mr-1">' + name + "</a>";
      } else {
        source = name;
      }
      if (source) {
        _references.push(source);
      }
    });
    return _references.join(",");
  }
};

let moreLessEventFncs = [];
const _cleanUpAddCardMoreLessEvents = () => {
  moreLessEventFncs.forEach((obj) => {
    if (obj && obj.elm && obj.fnc) {
      obj.elm.removeEventListener("click", obj.fnc);
    }
  });
};

const _addCardMoreLess = (refs, bodyName, showMore) => {
  lodashDebounce(() => {
    const elem = refs[bodyName];
    if (elem && elem.$el) {
      let elemsShown = 2;
      if (
        lodashGet(elem, "$el.children[0].innerText") &&
        elem.$el.children[0].innerText.split(" ").length > 30
      ) {
        elemsShown = 1;
      }

      let showMoreExists = false;
      elem.$el.children.forEach((elm) => {
        if (elm.className.includes("show-more")) {
          showMoreExists = true;
        }
      });

      if (elem.$el.children.length > elemsShown && !showMoreExists) {
        const divMore = document.createElement("DIV");
        divMore.innerHTML = "Show More";
        divMore.className = "show-more mt-3 mr-3 text-right";
        divMore.style.cursor = "pointer";

        const onClickFncMore = () => {
          _showHideBodyChildren(elem, false, elemsShown);
        };
        divMore.addEventListener("click", onClickFncMore, false);
        moreLessEventFncs.push({
          elm: divMore,
          fnc: onClickFncMore,
        });

        elem.$el.appendChild(divMore);

        const divLess = document.createElement("DIV");
        divLess.innerHTML = "Show Less";
        divLess.className = "show-less mt-3 mr-3 text-right";
        divLess.style.display = "none";
        divLess.style.cursor = "pointer";

        const onClickFncLess = () => {
          _showHideBodyChildren(elem, true, elemsShown);
        };
        divLess.addEventListener("click", onClickFncLess, false);
        moreLessEventFncs.push({
          elem: divLess,
          fnc: onClickFncLess,
        });

        elem.$el.appendChild(divLess);
      }

      if (!showMore) {
        _showHideBodyChildren(elem, true, elemsShown);
      }
    }
  }, 2000)();
};

const _showHideBodyChildren = (elem, hide, elemsShown) => {
  if (elem && elem.$el && elem.$el.children && elem.$el.children.length) {
    const len = elem.$el.children.length;
    for (let i = elemsShown; i < len; i++) {
      elem.$el.children[i].style.display = hide ? "none" : "block";
    }
    if (len > 1) {
      elem.$el.children[len - 2].style.display = hide ? "block" : "none";
    }
  }
};

const _fixDecimalValuesForSlider = (value, decimalPoint) => {
  const result = Number(value).toFixed(decimalPoint);
  const parsedResult = parseFloat(result);
  // When We transform 100 mi to km its saved as 160.93km but when We convert it back its 99.99 mi
  // This is for the slide so it shows 100 instead of 99
  return parsedResult === 99.9 ? 100.0 : parsedResult;
};

const _formatSearchRadius = (radius, distRadius) => {
  if (distRadius === "mi") {
    return _fixDecimalValuesForSlider(radius / MI_TO_KM_CONVERSION_FACTOR, 2);
  } else {
    return _fixDecimalValuesForSlider(radius, 2);
  }
};
